import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Row, Col, Badge, Button } from "reactstrap";
import { Line } from "react-chartjs-2";
import {
  CategoryScale,
  Chart,
  ChartData,
  ChartOptions,
  Filler,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import {
  EarningsEntry,
  FeaturedTeaser,
  PatternTeaser,
  daysUntil,
  useUpcomingEarnings,
} from "../hooks/useEarnings";
import { buildViewFeaturedUrl, buildPatternDetailUrl } from "../utils/links";
import { useTheme } from "../context/ThemeContext";
import { PEAK_KEY_POINTS, TROUGH_KEY_POINTS } from "../utils/patternKeyPoints";

// Only what the row sparkline draws (Home registers the full set for its own charts;
// registering twice is a no-op, and this keeps the panel self-contained). annotationPlugin
// draws the classic-pattern key-point triangles (see PatternKeyPointMarkers below).
Chart.register(CategoryScale, LinearScale, LineController, LineElement, PointElement, Filler, annotationPlugin);

// Window (trading days) → human label, matching the Earnings Calendar page.
const NUM_TO_LABEL: Record<number, string> = {
  10: "2w", 20: "4w", 65: "3m", 130: "6m", 195: "9m", 260: "1y", 22: "1m", 45: "2m",
};

const HOW_MANY = 5;

const prettyPattern = (t: string) =>
  t.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

type FreeSignal =
  | { kind: "featured"; featured: FeaturedTeaser }
  | { kind: "pattern"; pattern: PatternTeaser & { start_date: string; window: number } };

// Resolve the signal for an entry (featured preferred — it's the headline analog
// signal), or null if the entry carries no signal at all.
//
// No window filter: this used to drop featured 130/260 and pattern 130/195/260 because
// long windows were Pro-only. That gate was retired 2026-08-11 (window depth is no
// longer a paid axis — every window is free to everyone), so filtering here only threw
// away signals an anonymous viewer can open just fine.
function freeSignal(e: EarningsEntry): FreeSignal | null {
  const f = e.signals?.featured;
  if (f) {
    return { kind: "featured", featured: f };
  }
  const p = e.signals?.pattern;
  if (p && p.start_date && typeof p.window === "number") {
    return { kind: "pattern", pattern: { ...p, start_date: p.start_date, window: p.window } };
  }
  return null;
}

function whenLabel(dateStr: string): string {
  const d = daysUntil(dateStr);
  if (d <= 0) return "Today";
  if (d === 1) return "Tomorrow";
  return `In ${d} days`;
}

function urgencyColor(dateStr: string): string {
  const d = daysUntil(dateStr);
  if (d <= 1) return "danger";
  if (d <= 3) return "warning";
  return "secondary";
}

// Drives BOTH the row's left border (CSS class) and the chart line color, so a bearish
// pattern reads red like a bearish analog. Patterns without a sentiment (neutral, or an
// older payload missing the field) keep the neutral blue.
function sentimentClass(sig: FreeSignal): SeriesTone {
  if (sig.kind === "featured") return sig.featured.direction === "up" ? "up" : "down";
  const s = sig.pattern.sentiment;
  if (s === "bullish") return "up";
  if (s === "bearish") return "down";
  return "pattern";
}

function signalTag(sig: FreeSignal): string {
  if (sig.kind === "featured") {
    const label = NUM_TO_LABEL[sig.featured.window] ?? sig.featured.window;
    return `${sig.featured.direction === "up" ? "Bullish" : "Bearish"} · ${label} analog`;
  }
  return `Pattern · ${prettyPattern(sig.pattern.pattern_type)}`;
}

function detailLink(ticker: string, sig: FreeSignal): string {
  if (sig.kind === "featured") {
    // unlock token → the logged-out click opens this signal in full (like a share link).
    return buildViewFeaturedUrl({ ticker, direction: sig.featured.direction, window: sig.featured.window, unlock: sig.featured.unlock });
  }
  const p = sig.pattern;
  return buildPatternDetailUrl({ ticker, patternType: p.pattern_type, startDate: p.start_date, window: p.window, lookbackDays: 7, unlock: p.unlock });
}

type SeriesTone = "up" | "down" | "pattern";

// Mirrors --success / --error / --primary in tokens.css, per theme: the line sits right
// beside the row's token-colored left border, so a fixed palette would visibly disagree
// with it in dark mode (where --success/--error brighten; --primary doesn't shift).
const SERIES_COLORS: Record<"light" | "dark", Record<SeriesTone, { line: string; fill: string }>> = {
  light: {
    up: { line: "#059669", fill: "rgba(5, 150, 105, 0.12)" },
    down: { line: "#DC2626", fill: "rgba(220, 38, 38, 0.12)" },
    pattern: { line: "#3B82F6", fill: "rgba(59, 130, 246, 0.12)" },
  },
  dark: {
    up: { line: "#34D399", fill: "rgba(52, 211, 153, 0.12)" },
    down: { line: "#F87171", fill: "rgba(248, 113, 113, 0.12)" },
    pattern: { line: "#3B82F6", fill: "rgba(59, 130, 246, 0.12)" },
  },
};

const MS_PER_DAY = 86_400_000;

/** Local (not UTC) YYYY-MM-DD — toISOString would shift the date near midnight. */
function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Trading days → calendar days, with slack for weekends and holidays. */
const calendarSpan = (tradingDays: number) => Math.ceil(tradingDays * 1.5) + 10;

/**
 * Price-context request for a signal's chart window. `/api/patterns/price-context` is
 * public and ungated (raw OHLCV, no signal content), so this renders for anonymous
 * viewers too — which is why the row can't just read the gated /api/featured payload.
 * Featured signals chart the trailing `window` sessions; patterns chart the formation
 * span plus a few days of context on each side.
 */
function seriesRequest(ticker: string, sig: FreeSignal): { url: string; take: number | null } {
  const enc = encodeURIComponent(ticker);
  const today = new Date();
  if (sig.kind === "featured") {
    const window = sig.featured.window;
    const start = new Date(today.getTime() - calendarSpan(window) * MS_PER_DAY);
    return {
      url: `/api/patterns/price-context?ticker=${enc}&start_date=${isoDate(start)}&end_date=${isoDate(today)}&context_days=0`,
      take: window,
    };
  }
  const p = sig.pattern;
  const startMs = new Date(`${p.start_date}T00:00:00`).getTime();
  const end = new Date(Math.min(today.getTime(), startMs + calendarSpan(p.window) * MS_PER_DAY));
  return {
    url: `/api/patterns/price-context?ticker=${enc}&start_date=${p.start_date}&end_date=${isoDate(end)}&context_days=5`,
    take: null,
  };
}

/**
 * For a classic-pattern row only: the request that fetches the pattern's `key_points` (the
 * structural highs/lows that identify it — e.g. the two peaks of a Double Top), so the row's
 * chart can mark them the same way PatternDetail does. `/api/patterns` skips the anon
 * ticker-obfuscation tease whenever a `ticker` filter is present (it's a detail lookup, not a
 * browse listing — see resources/patterns.py `_gate_pattern_rows`), so this needs no unlock
 * token to see the real data; passed anyway for consistency with PatternDetail's own fetch.
 * `lookback_days=7` mirrors the window `signals_for_tickers` already uses to consider a
 * pattern "recent" (the detection may be a few days old, not necessarily today's).
 */
function patternKeyPointsQuery(ticker: string, sig: FreeSignal): { url: string; startDate: string } | null {
  if (sig.kind !== "pattern") return null;
  const p = sig.pattern;
  const enc = encodeURIComponent(ticker);
  const unlockQuery = p.unlock ? `&unlock=${encodeURIComponent(p.unlock)}` : "";
  const url = `/api/patterns?ticker=${enc}&pattern_type=${encodeURIComponent(p.pattern_type)}` +
    `&min_confidence=0&limit=10&lookback_days=7&window=${p.window}${unlockQuery}`;
  return { url, startDate: p.start_date };
}

// Same visual language as PatternDetail's candle-mode key points (pink/red triangle, ▼
// above a peak, ▲ below a trough) — smaller and unlabeled, since the row chart is only
// 140px tall and a text label per point would be unreadable clutter at that size.
const KEY_POINT_MARKER = {
  radius: 5,
  backgroundColor: "rgba(255, 99, 132, 0.85)",
  borderColor: "rgba(255, 99, 132, 1)",
  borderWidth: 1,
};

const CHART_OPTIONS: ChartOptions<"line"> = {
  responsive: true,
  maintainAspectRatio: false,
  animation: false,
  // The whole row is one link, so the chart must not capture pointer intent.
  events: [],
  plugins: { legend: { display: false }, tooltip: { enabled: false } },
  scales: {
    x: { display: false },
    // Without headroom Chart.js fits y to the exact data min/max, so the extremes land
    // on the canvas edge and the 2px stroke is visibly clipped there.
    y: { display: false, grace: "10%" },
  },
};

/**
 * Per-row chart — a real Chart.js line of the signal window's closes, drawn in the same
 * style as the rest of the site's charts. (This was previously an <img> of the OG social
 * card: a 1200×630 PNG with its own typography and branding, crammed into a fixed
 * 360×192 box and cropped by object-fit. The OG endpoints are untouched and still serve
 * social unfurls / crawlers via /api/share/*.) Renders nothing if the series can't be
 * fetched, so a bad ticker collapses the slot instead of leaving a broken box.
 */
function SignalChart({ ticker, sig }: { ticker: string; sig: FreeSignal }) {
  const { url, take } = seriesRequest(ticker, sig);
  const { theme } = useTheme();
  const [closes, setCloses] = useState<number[] | null>(null);
  const [patternStartIndex, setPatternStartIndex] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    // Plain fetch (not the axios instance) — no Authorization header, so the response
    // stays edge-cacheable.
    fetch(url)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d: { prices?: (number | null)[]; pattern_start_index?: number | null }) => {
        if (!alive) return;
        const prices = (d.prices ?? []).filter((p): p is number => typeof p === "number");
        const series = take ? prices.slice(-take) : prices;
        if (series.length < 2) setFailed(true);
        else setCloses(series);
        setPatternStartIndex(d.pattern_start_index ?? null);
      })
      .catch(() => {
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [url, take]);

  const kp = patternKeyPointsQuery(ticker, sig);
  const kpUrl = kp?.url ?? null;
  const kpStartDate = kp?.startDate ?? null;
  const [keyPoints, setKeyPoints] = useState<Record<string, number> | null>(null);

  useEffect(() => {
    if (!kpUrl) {
      setKeyPoints(null);
      return;
    }
    let alive = true;
    fetch(kpUrl)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d: { patterns?: PatternMatch[] }) => {
        if (!alive) return;
        const rows = d.patterns ?? [];
        const match = rows.find((row) => row.start_date === kpStartDate) ?? rows[0];
        setKeyPoints(match?.key_points ?? null);
      })
      .catch(() => {
        // Best-effort enhancement — a failed fetch just means no triangle markers, the
        // line chart itself (already fetched separately above) renders regardless.
        if (alive) setKeyPoints(null);
      });
    return () => {
      alive = false;
    };
  }, [kpUrl, kpStartDate]);

  // Triangle markers for the pattern's structural highs/lows (▼ peak, ▲ trough — same
  // convention as PatternDetail's candle-mode key points), positioned on this row's own
  // close-price series. Skips any key point outside PEAK/TROUGH_KEY_POINTS (event points
  // like a neckline break have no established up/down orientation) or out of range.
  const keyPointAnnotations = useMemo(() => {
    if (!keyPoints || closes === null || patternStartIndex === null) return null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const out: Record<string, any> = {};
    Object.entries(keyPoints).forEach(([pointName, relativeIndex]) => {
      const isPeak = PEAK_KEY_POINTS.has(pointName);
      const isTrough = TROUGH_KEY_POINTS.has(pointName);
      if (!isPeak && !isTrough) return;
      const absoluteIndex = patternStartIndex + relativeIndex;
      if (absoluteIndex < 0 || absoluteIndex >= closes.length) return;
      out[`kp_${pointName}`] = {
        type: "point",
        xValue: absoluteIndex,
        yValue: closes[absoluteIndex],
        pointStyle: "triangle",
        rotation: isPeak ? 180 : 0,
        ...KEY_POINT_MARKER,
      };
    });
    return Object.keys(out).length > 0 ? out : null;
  }, [keyPoints, closes, patternStartIndex]);

  const chartOptions = useMemo<ChartOptions<"line">>(() => {
    if (!keyPointAnnotations) return CHART_OPTIONS;
    return {
      ...CHART_OPTIONS,
      plugins: {
        ...CHART_OPTIONS.plugins,
        annotation: { annotations: keyPointAnnotations },
      },
    };
  }, [keyPointAnnotations]);

  if (failed) return null;

  const tone = SERIES_COLORS[theme][sentimentClass(sig)];

  return (
    <div className="earnings-signal-chart-wrap">
      {closes === null ? (
        <div className="skeleton earnings-signal-chart-skeleton" />
      ) : (
        <Line
          data={
            {
              labels: closes.map((_, i) => i),
              datasets: [
                {
                  data: closes,
                  borderColor: tone.line,
                  backgroundColor: tone.fill,
                  borderWidth: 2,
                  pointRadius: 0,
                  tension: 0.3,
                  fill: true,
                },
              ],
            } as ChartData<"line", number[], number>
          }
          options={chartOptions}
        />
      )}
    </div>
  );
}

/** Placeholder rows so the panel reserves its height while /api/earnings is in flight. */
function SkeletonRows() {
  return (
    <>
      {Array.from({ length: HOW_MANY }, (_, i) => (
        <Col xs="12" className="mb-2" key={i}>
          <div className="earnings-signal-row">
            <div className="earnings-signal-chart-wrap">
              <div className="skeleton earnings-signal-chart-skeleton" />
            </div>
            <div className="earnings-signal-details">
              <div className="earnings-signal-id">
                <div className="skeleton skeleton-line short" />
                <div className="skeleton skeleton-line medium thin" />
              </div>
              <div className="skeleton skeleton-line earnings-signal-meta-skeleton" />
            </div>
          </div>
        </Col>
      ))}
    </>
  );
}

/**
 * Homepage panel: the next few upcoming-earnings tickers that currently carry a
 * DejaChart signal — every window, all of them free to view for any visitor.
 * Renders just the panel content (heading + stacked rows) so Home can place it
 * beside the Sample Matches panel; renders nothing when there are no qualifying
 * tickers.
 */
export default function HomeEarningsSignals() {
  const { entries, loading } = useUpcomingEarnings();

  const picks = entries
    .map((e) => ({ e, sig: freeSignal(e) }))
    .filter((x): x is { e: EarningsEntry; sig: FreeSignal } => x.sig !== null)
    .slice(0, HOW_MANY);

  if (!loading && picks.length === 0) return null;

  return (
    <div className="home-panel">
      <h2 className="section-title">Reporting Soon — With a Signal</h2>
      <p className="section-subtitle text-muted text-center mb-4">
        Upcoming earnings on stocks that currently have a DejaChart analog or chart
        pattern signal. Free to view for everyone.
      </p>
      <Row>
        {loading && <SkeletonRows />}
        {!loading && picks.map(({ e, sig }) => (
          <Col xs="12" className="mb-2" key={e.ticker}>
            <Link
              to={detailLink(e.ticker, sig)}
              className={`earnings-signal-row earnings-signal-row-${sentimentClass(sig)}`}
            >
              <SignalChart ticker={e.ticker} sig={sig} />
              <div className="earnings-signal-details">
                <div className="earnings-signal-id">
                  <div className="earnings-signal-ticker">{e.ticker}</div>
                  <div className="earnings-signal-name" title={e.name}>
                    {e.name}
                  </div>
                </div>
                <div className="earnings-signal-meta">
                  <span className="earnings-signal-tag">{signalTag(sig)}</span>
                  <Badge color={urgencyColor(e.date)} pill>
                    {whenLabel(e.date)}
                  </Badge>
                </div>
              </div>
            </Link>
          </Col>
        ))}
      </Row>
      <div className="text-center mt-4">
        <Link to="/earnings">
          <Button color="primary" className="primary-btn">
            See the Full Earnings Calendar
          </Button>
        </Link>
      </div>
      <div className="text-center mt-2">
        <Link to="/patterns" className="earnings-secondary-link">
          Explore All Classic Patterns →
        </Link>
      </div>
    </div>
  );
}
